import { useEffect, useMemo } from 'react';

import { useAppearanceSaves } from '../../contexts/appearance-saves';
import { useSession } from '../../contexts/session';
import { grounds, themeCookie } from '../../theme';

// A year: the cookie only has to outlive the gap between two visits, and the
// account's own choice rewrites it on every visit anyway.
const cookieAge = 365 * 24 * 60 * 60;

// Keeps the document in the theme the signed-in account chose (C27), as the
// service last acknowledged it through the appearance saves (#136): Device
// follows the device's light or dark setting, Light and Dark fix it. It draws
// nothing; it sets data-theme on <html>, the theme-color the browser paints
// around the page, and the psy_theme cookie the server reads to draw the next
// document in the same theme from the first paint.
//
// Signed out, the theme follows the device again and the cookie goes, so the
// sign-in screen never keeps the last person's choice. While the first answer
// about the session is on its way, or when it failed, nothing is touched and
// the server's guess from the cookie stands.
export function ApplyAppearance() {
  const { me } = useSession();
  const { applied } = useAppearanceSaves();

  // A string, so the effect below runs when the choice changes and not on
  // every new answer about the session.
  const appearance = useMemo(function() {
    if (me === undefined) {
      return;
    }

    if (me === null) {
      return 'DEVICE';
    }

    return applied;
  }, [ me, applied ]);

  useEffect(function() {
    if (appearance == null) {
      return;
    }

    const root = document.documentElement;
    const tags = document.querySelectorAll('meta[name="theme-color"]');

    if (appearance === 'DEVICE') {
      delete root.dataset.theme;
      document.cookie = `${ themeCookie }=; Path=/; Max-Age=0; SameSite=Lax`;

      // Each tag answers one device setting again.
      tags.forEach(function(tag) {
        tag.content = grounds.light;

        if (tag.media === '(prefers-color-scheme: dark)') {
          tag.content = grounds.dark;
        }
      });

      return;
    }

    const theme = appearance.toLowerCase();

    // Secure only over HTTPS, as auth does for the session cookie: a Secure
    // cookie is dropped on plain http, which is how development is served.
    let secure = '';

    if (location.protocol === 'https:') {
      secure = '; Secure';
    }

    root.dataset.theme = theme;
    document.cookie = `${ themeCookie }=${ theme }; Path=/; Max-Age=${ cookieAge }; SameSite=Lax${ secure }`;

    tags.forEach(function(tag) {
      tag.content = grounds[ theme ];
    });
  }, [ appearance ]);

  return null;
}
